import { useQueryClient } from '@tanstack/react-query'
import { AxiosError, AxiosResponse } from 'axios'
import TextFieldWithUnit from 'common/components/TextFieldWithUnit'
import ThaiDatePicker from 'common/components/ThaiDatePicker'
import { getWaterMineralKey } from 'common/constants/queryKeys'
import useAnalytics, { AnalyticsEvents } from 'common/hooks/useAnalytics'
import useYupValidationResolver from 'common/hooks/useYupValidationResolver'
import { formatDate } from 'common/utils/date'
import { errorMessageByField } from 'common/utils/hook-form'
import { numberSchema } from 'common/utils/yup'
import endOfToday from 'date-fns/endOfToday'
import parseISO from 'date-fns/parseISO'
import BaseRecordDialog from 'modules/pond/components/BaseRecordDialog'
import { useSpeciesName } from 'modules/pond/api/selector'
import { useMutateCreateWaterMineral, useMutatePatchWaterMineral } from 'modules/water/api/mutation'
import { WaterEditData, WaterMineral } from 'modules/water/api/types'
import { MineralType, MINERALS } from 'modules/water/constants'
import { waterDateFilterState } from 'modules/water/stores/atom'
import { normalizeSpeciesName } from 'utils/analytics'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useRecoilValue } from 'recoil'
import * as Yup from 'yup'

import { Box, Grid } from '@mui/material'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

interface MineralRecordDialogProps {
  onClose: () => void
  open: boolean
  isEdit?: boolean
  onSuccessSave?: () => void
  editData?: WaterEditData
}

interface FormDataType {
  date: Date
  [MineralType.Calcium]: string
  [MineralType.Magnesium]: string
  [MineralType.Potassium]: string
}

const MineralRecordDialog = ({ onClose, open, isEdit, editData }: MineralRecordDialogProps) => {
  const { t } = useTranslation(['water', 'common'])
  const [submitting, setSubmitting] = useState(false)

  const qc = useQueryClient()
  const date = useRecoilValue(waterDateFilterState)
  const mineralEditData = editData as WaterMineral | undefined

  const recordWaterMineralMutation = useMutateCreateWaterMineral()
  const updateWaterMineralMutation = useMutatePatchWaterMineral()
  const speciesName = useSpeciesName()
  const { trackEvent } = useAnalytics()

  const schema = Yup.object().shape({
    date: Yup.date(),
    [MineralType.Calcium]: numberSchema(t('require.number')),
    [MineralType.Magnesium]: numberSchema(t('require.number')),
    [MineralType.Potassium]: numberSchema(t('require.number')),
  })

  const resolver = useYupValidationResolver(schema)

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<FormDataType>({
    defaultValues: {
      date: new Date(),
      [MineralType.Calcium]: undefined,
      [MineralType.Magnesium]: undefined,
      [MineralType.Potassium]: undefined,
    },
    resolver,
  })

  useEffect(() => {
    if (date) {
      reset((values) => ({
        ...values,
        date: date,
      }))
    }
  }, [date])

  useEffect(() => {
    if (mineralEditData && isEdit) {
      reset((_val) => ({
        date: parseISO(mineralEditData.date),
        [MineralType.Calcium]: mineralEditData.calcium !== null ? `${mineralEditData.calcium}` : '',
        [MineralType.Magnesium]:
          mineralEditData.magnesium !== null ? `${mineralEditData.magnesium}` : '',
        [MineralType.Potassium]:
          mineralEditData.potassium !== null ? `${mineralEditData.potassium}` : '',
      }))
    }
  }, [mineralEditData, isEdit])

  const onSuccess = (res: AxiosResponse) => {
    const { status } = res.data
    if (status === 200) {
      setSubmitting(false)
      onClose()
      reset()
      qc.invalidateQueries({ queryKey: getWaterMineralKey.all() })
      const normalizedSpecies = normalizeSpeciesName(speciesName)
      trackEvent(isEdit ? AnalyticsEvents.waterQualityRecordUpdated : AnalyticsEvents.waterQualityRecordCreated, {
        water_record_type: 'mineral',
        ...(normalizedSpecies ? { species: normalizedSpecies } : {}),
      })
    }
  }

  const onError = (error: unknown, _variables: any, _context: unknown) => {
    const { response } = error as AxiosError
    if (response?.status === 400) {
      setError('date', {
        type: 'server',
        message: t('water:duplicate.record'),
      })
    }
  }

  const onSubmit = async (data: FormDataType) => {
    setSubmitting(true)
    const formattedDate = formatDate(data.date)
    const calcium = Number(data.calcium)
    const magnesium = Number(data.magnesium)
    const potassium = Number(data.potassium)
    if (isEdit) {
      await updateWaterMineralMutation
        .mutateAsync(
          {
            mineralId: Number(mineralEditData?.id),
            body: {
              date: formattedDate,
              calcium,
              magnesium,
              potassium,
            },
          },
          { onSuccess, onError }
        )
        .finally(() => setSubmitting(false))
      return
    }

    await recordWaterMineralMutation
      .mutateAsync(
        {
          body: {
            date: formattedDate,
            calcium,
            magnesium,
            potassium,
          },
        },
        {
          onSuccess,
          onError,
        }
      )
      .finally(() => setSubmitting(false))
  }

  return (
    <BaseRecordDialog
      onClose={onClose}
      open={open}
      formId="record-mineral"
      isSubmitting={submitting}
      title={t('water:record.mineral')}
      isShowPondName={false}
      enableCloseButton={true}
      submitButtonLabel={t('common:submit')}
    >
      <form id="record-mineral" onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2}>
          <Grid item xs={6}>
            <Box sx={{ paddingTop: 1, mb: 1 }}>
              <Typography fontSize={16} fontWeight={600}>
                <Typography component="span" color="error" sx={{ mr: 0.5 }}>
                  *
                </Typography>
                {t('common:date')}
              </Typography>
            </Box>
            <Controller
              name="date"
              control={control}
              render={({ field }) => (
                <ThaiDatePicker
                  {...field}
                  format="dd/MM/yyyy"
                  maxDate={endOfToday()}
                  slotProps={{
                    textField: {
                      id: 'mineral-date-picker',
                      size: 'small',
                      error: !!errors.date,
                      helperText: errorMessageByField('date', errors),
                      fullWidth: true,
                    },
                  }}
                  readOnly={isEdit}
                />
              )}
            />
          </Grid>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              columnGap: '20px',
              rowGap: '16px',
            }}
          >
            {MINERALS.map((el) => (
              <Stack
                key={el.name}
                direction="column"
                spacing={0.5} // 4px
                alignItems="start"
              >
                <Typography fontSize={16} fontWeight={600} sx={{ pt: 1, mb: 1 }}>
                  {t(el.label)}
                </Typography>

                <TextFieldWithUnit
                  name={el.name}
                  control={control}
                  errors={errors}
                  placeholder={t('common:input.amount')}
                  unit={t(el.unit)}
                />
              </Stack>
            ))}
          </Box>
        </Stack>
      </form>
    </BaseRecordDialog>
  )
}

export default MineralRecordDialog
